import moment from 'moment';

/**
 * Session identifiers for the nexa-client diagnostic page. When this shape
 * is passed, the deep link opens the selected session's timeline directly.
 * `sessionStartMs` is the raw millisecond timestamp from the
 * row (IFS emits it as SessionStartTimestampMs); nexa-agent's Recipe A prefers
 * it verbatim over parsing `sessionStart` (ISO).
 */
export interface SessionDrillInput {
  sessionId: string | number;
  clientId: string | number;
  sessionStartMs: number;
  /** Optional end timestamp; omitted for ongoing sessions or invalid bounds. */
  sessionEndMs?: number | null;
  /** Optional ISO fallback for older nexa-client builds that don't read `session_start_ms`. */
  sessionStart?: string | null;
}

export interface ViewerNexaUrlInput {
  viewerId?: string | number | null;
  from?: string | number | null;
  to?: string | number | null;
  customerId?: string | number | null;
  session?: SessionDrillInput | null;
}

/** Normalize a date value (ISO string, ms epoch, or moment-parseable) to ISO 8601 UTC, or null.
 *  Rejects non-scalar inputs at runtime: moment({}) silently returns "now" instead of
 *  invalid, which would let a garbage upstream shape masquerade as a valid timestamp. */
export function toIso(value?: string | number | null): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  if (typeof value === 'number' && !Number.isFinite(value)) return null;
  const m = moment(value);
  return m.isValid() ? m.toISOString() : null;
}

/** Coerce an upstream `sessionStartTime` (unknown shape from IFS rows) into a
 *  positive-finite ms epoch, or null. Explicitly rejects objects/arrays so
 *  moment(...) never fills in the current time from a missing-fields object. */
export function toStartMs(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  if (typeof value === 'string' && value.length === 0) return null;
  if (typeof value === 'number' && !Number.isFinite(value)) return null;
  const ms = moment(value).valueOf();
  return Number.isFinite(ms) && ms > 0 ? ms : null;
}

const isPresent = (v?: string | number | null): boolean =>
  v !== null && v !== undefined && `${v}` !== '';

function isDrillPresent(session?: SessionDrillInput | null): boolean {
  if (!session) return false;
  const ms = Number(session.sessionStartMs);
  return (
    isPresent(session.sessionId) && isPresent(session.clientId) && Number.isFinite(ms) && ms > 0
  );
}

/**
 * Build a cross-app URL into the nexa-client viewer page.
 *
 * - viewerId + session drill present -> /app/nexa/dashboard/viewer/<id>/diagnostic?session_id=…&client_id=…&session_start_ms=…
 *                                       (opens the selected session's timeline)
 * - viewerId only                    -> /app/nexa/dashboard/viewer/<id>?interval=<from>/<to>&customer=<cid>
 *                                       (fires the viewer overview)
 * - no viewerId                      -> /app/nexa/dashboard/viewer?customer=<cid>
 *
 * Viewer overviews carry `interval` when BOTH dates normalize to valid ISO,
 * and `autosubmit=true` to run the overview. Diagnostics use session timestamps
 * rather than the overview's date selection.
 */
export function buildViewerNexaUrl(input: ViewerNexaUrlInput): string {
  const { viewerId, from, to, customerId, session } = input;
  const origin = window.location.origin;
  const params = new URLSearchParams();

  const fromIso = toIso(from);
  const toIsoVal = toIso(to);
  const hasViewer = isPresent(viewerId);
  const hasDrill = hasViewer && isDrillPresent(session);

  if (hasViewer && !hasDrill && fromIso && toIsoVal) {
    params.set('interval', `${fromIso}/${toIsoVal}`);
  }
  if (isPresent(customerId)) {
    params.set('customer', `${customerId}`);
  }
  if (hasDrill) {
    // The drill triplet is only meaningful when a viewer scope is also set —
    // Recipe A on the agent needs all three plus the viewer id in context.
    params.set('session_id', `${session!.sessionId}`);
    params.set('client_id', `${session!.clientId}`);
    params.set('session_start_ms', `${Math.trunc(Number(session!.sessionStartMs))}`);
    const endMs = session!.sessionEndMs;
    if (
      typeof endMs === 'number' &&
      Number.isFinite(endMs) &&
      endMs > Number(session!.sessionStartMs)
    ) {
      params.set('session_end_ms', `${Math.trunc(endMs)}`);
    }
    const sessionStartIso = session!.sessionStart && toIso(session!.sessionStart);
    if (sessionStartIso) {
      // Older nexa-client builds don't read `session_start_ms`; keep the ISO
      // as a fallback so they can still fire Recipe A (with slightly worse
      // precision) rather than falling through to the overview.
      params.set('session_start', sessionStartIso);
    }
  }
  if (!hasDrill) params.set('autosubmit', 'true');

  const path = hasViewer
    ? `/app/nexa/dashboard/viewer/${encodeURIComponent(`${viewerId}`)}${hasDrill ? '/diagnostic' : ''}`
    : '/app/nexa/dashboard/viewer';
  const qs = params.toString();
  return `${origin}${path}${qs ? `?${qs}` : ''}`;
}
